import { EventsManager } from '@game-libs/events-manager';
import { useGameDispatch } from '../global-context';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { QuickGameDialog } from '@game-libs/quick-game-dialog';
import { CONFIG } from '../../config';
import { DungeonEvents, GameplayEvents } from '../../types/game-events';
import { GUIActions, UpgradeActions, UpgradeType } from '../../types/game-types';
import { GlobalGameStore } from '@game-libs/global-game-store';
import { getRandomThing, humanNumber } from '@game-libs/exc-libs';
import { TimedEventsOrchestrator } from '@game-libs/timed-events-orchestrator';
import { useTimedEventsStore } from '../zustand/useTimedEventsStore';
import { useUpgradesStore } from '../zustand/useUpgradesStore';
import { useGeneratorsStore } from '../zustand/useGeneratorsStore';
import { useGameStatusStore } from '../zustand/useGameStatusStore';
import { AudioManager } from '@game-libs/audio-manager';

/**
 * Turn `total` reinforcements into three integer counts matching the player's warrior / wizard / elf mix.
 * Uses largest remainder so the three values always sum to `total` (ratio × total, then fix rounding).
 */
function splitReinforcementsByOwnedMix(
  ownedWarriors: number,
  ownedWizards: number,
  ownedElves: number,
  total: number,
): { sentWarriors: number; sentWizards: number; sentElves: number } {
  const totalOwned = ownedWarriors + ownedWizards + ownedElves;
  if (totalOwned <= 0) {
    const base = Math.floor(total / 3);
    const remainderSlots = total - base * 3;
    return {
      sentWarriors: base + (remainderSlots > 0 ? 1 : 0),
      sentWizards: base + (remainderSlots > 1 ? 1 : 0),
      sentElves: base,
    };
  }

  const exactWarriors = (ownedWarriors / totalOwned) * total;
  const exactWizards = (ownedWizards / totalOwned) * total;
  const exactElves = (ownedElves / totalOwned) * total;

  const floorWarriors = Math.floor(exactWarriors);
  const floorWizards = Math.floor(exactWizards);
  const floorElves = Math.floor(exactElves);

  let remainderToAllocate = total - (floorWarriors + floorWizards + floorElves);

  const orderedByLargestFractionalPart = [
    { key: 'warriors' as const, floor: floorWarriors, frac: exactWarriors - floorWarriors },
    { key: 'wizards' as const, floor: floorWizards, frac: exactWizards - floorWizards },
    { key: 'elves' as const, floor: floorElves, frac: exactElves - floorElves },
  ].sort((left, right) => right.frac - left.frac);

  let sentWarriors = floorWarriors;
  let sentWizards = floorWizards;
  let sentElves = floorElves;

  for (let allocationIndex = 0; allocationIndex < remainderToAllocate; allocationIndex++) {
    const targetKey = orderedByLargestFractionalPart[allocationIndex].key;
    if (targetKey === 'warriors') {
      sentWarriors += 1;
    } else if (targetKey === 'wizards') {
      sentWizards += 1;
    } else {
      sentElves += 1;
    }
  }

  return { sentWarriors, sentWizards, sentElves };
}

const catsSFXList = [
  { value: 'cat_1', probability: 25 },
  { value: 'cat_2', probability: 25 },
  { value: 'cat_3', probability: 20 },
  { value: 'cat_4', probability: 25 },
];

export const useHandleTimedEvents = () => {
  const dispatch = useGameDispatch();
  const catapultCatsUpgrade = useUpgradesStore((store) => store.upgrades.catapult.cats);
  const { actions, state: variablesState } = GlobalGameStore();
  const { playSFX } = AudioManager();
  const { pauseGroup, unpauseGroup, setEventRate } = TimedEventsOrchestrator();
  const { i18n } = useTranslation();

  const { on, once } = EventsManager();

  // Timed Events Listeners

  useEffect(() => {
    const { openDialog } = QuickGameDialog({
      pathToInkFiles: './dialogs',
      pathToSpeakerFiles: './dialogs/pictures',
      containerId: 'ui-wrapper',
    });

    const roarUnsub = on(GameplayEvents.DRAGON_ROAR, () => {
      dispatch({ type: GUIActions.ADD_STUN_EFFECT });
    });

    once('dungeon_keys', () => {
      openDialog('dungeon_keys', i18n.language, false);
    });

    once('give_keys', () => {
      dispatch({
        type: GUIActions.ADD_DUNGEON_REWARDS,
        payload: {
          currentLevel: 1,
          gold: 0,
          keys: 20,
        },
      });
    });

    once('end_give_keys', () => {
      unpauseGroup('dialog');
    });

    once('invasion_start', () => {

      const currentGold = variablesState.globalVariables.value.gold;

      const ransomCost = Math.ceil((currentGold * 0.85) / 100) * 100;

      const { warrior, wizard, elf } = useGeneratorsStore.getState().generators;

      const hasEnoughManyUnits = warrior.owned + wizard.owned + elf.owned >= 1000;
      const hasEnoughFewUnits = warrior.owned + wizard.owned + elf.owned >= 200;

      actions.setGlobalVariables({
        ransomCost: ransomCost,
        ransomCostLabel: humanNumber(ransomCost),
        unitsCountFew: hasEnoughFewUnits ? 200 : -1,
        unitsCountMany: hasEnoughManyUnits ? 200 * 5 : -1,
      });

      openDialog('invasion_start', i18n.language, false);
    });

    once('invasion_end', () => {
      openDialog('invasion_end', i18n.language, false);

      if (!variablesState.globalVariables.value.strategyChoice) {
        console.warn('No strategy chosen');
        return;
      }

      const chosenStrategy = variablesState.globalVariables.value.strategyChoice;

      switch (chosenStrategy) {
        case 'pay_enemy':
          return;

        case 'send_few_units':
          dispatch({
            type: GUIActions.SET_GOLD,
            payload: -variablesState.globalVariables.value.ransomCost,
          });

          useGameStatusStore.getState().setLastInvasionManyUnitsSent(null);
          break;

        case 'send_many_units': {
          const storedSent = useGameStatusStore.getState().lastInvasionManyUnitsSent;
          if (storedSent === null) {
            console.warn('invasion_end send_many_units: missing lastInvasionManyUnitsSent');
            break;
          }

          // Half of what was removed return as reinforcements (integers per type).
          const returnedWarriors = Math.floor(storedSent.warriors / 2);
          const returnedWizards = Math.floor(storedSent.wizards / 2);
          const returnedElves = Math.floor(storedSent.elves / 2);

          dispatch({
            type: GUIActions.ADD_GENERATORS,
            payload: {
              warriors: returnedWarriors,
              wizards: returnedWizards,
              elves: returnedElves,
            },
          });

          useGameStatusStore.getState().setLastInvasionManyUnitsSent(null);
          break;
        }

        default:
          break;
      }
    });

    once('pope_visit', () => {
      openDialog('pope_visit', i18n.language, false);

      const currentGold = variablesState.globalVariables.value.gold;
      const contributionCost = Math.ceil((currentGold * 0.85) / 100) * 100;

      actions.setGlobalVariables({
        contributionCost: contributionCost,
        contributionCostLabel: humanNumber(contributionCost),
      });
    });

    once('catapult', () => {
      openDialog('catapult', i18n.language, false);

      const currentGold = variablesState.globalVariables.value.gold;

      const catapultCost = Math.ceil((currentGold * 0.85) / 100) * 100;
      actions.setGlobalVariables({
        catapultCost: catapultCost,
        catapultCostLabel: humanNumber(catapultCost),
      });
    });

    once('shoot_cats', () => {
      dispatch({ type: UpgradeActions.BUY_UPGRADE, payload: { id: UpgradeType.Cats, entityType: 'catapult' } });

      actions.setGlobalVariables({ shootsCats: true, catSounds: true });
    });

    once('pay_enemy', () => {
      // removeEvent('invasion_end');
      dispatch({
        type: GUIActions.SET_GOLD,
        payload: -variablesState.globalVariables.value.ransomCost,
      });
    });

    once('send_few_units', () => {
      const unitsFewTotal = variablesState.globalVariables.value.unitsCountFew;
      const generatorsBeforeSendFew = useGeneratorsStore.getState().generators;
      const { sentWarriors, sentWizards, sentElves } = splitReinforcementsByOwnedMix(
        generatorsBeforeSendFew.warrior.owned,
        generatorsBeforeSendFew.wizard.owned,
        generatorsBeforeSendFew.elf.owned,
        unitsFewTotal,
      );

      const timedEventsOrchestrator = TimedEventsOrchestrator();
      timedEventsOrchestrator.addEvents([
        {
          id: 'invasion_end',
          iconId: 'damage',
          categoryId: 'dialog',
          emitId: 'invasion_end',
          group: 'dialog',
          pauseGroupOnActivation: true,
          duration: 6.5 * 60,
          elapsed: 0,
          once: true,
        },
      ]);
      useTimedEventsStore.getState().syncFromOrchestrator();

      dispatch({
        type: GUIActions.REMOVE_GENERATORS,
        payload: {
          warriors: -sentWarriors,
          wizards: -sentWizards,
          elves: -sentElves,
        },
      });
    });

    once('send_many_units', () => {
      const unitsManyTotal = variablesState.globalVariables.value.unitsCountMany;
      const generatorsBeforeSend = useGeneratorsStore.getState().generators;
      const { sentWarriors, sentWizards, sentElves } = splitReinforcementsByOwnedMix(
        generatorsBeforeSend.warrior.owned,
        generatorsBeforeSend.wizard.owned,
        generatorsBeforeSend.elf.owned,
        unitsManyTotal,
      );

      useGameStatusStore.getState().setLastInvasionManyUnitsSent({
        warriors: sentWarriors,
        wizards: sentWizards,
        elves: sentElves,
      });

      const timedEventsOrchestratorMany = TimedEventsOrchestrator();
      timedEventsOrchestratorMany.addEvents([
        {
          id: 'invasion_end',
          categoryId: 'dialog',
          emitId: 'invasion_end',
          group: 'dialog',
          pauseGroupOnActivation: true,
          duration: 7 * 60,
          elapsed: 0,
          once: true,
        },
      ]);
      useTimedEventsStore.getState().syncFromOrchestrator();

      dispatch({
        type: GUIActions.REMOVE_GENERATORS,
        payload: {
          warriors: -sentWarriors,
          wizards: -sentWizards,
          elves: -sentElves,
        },
      });
    });

    once('pay_contribution', () => {
      dispatch({
        type: GUIActions.SET_GOLD,
        payload: -variablesState.globalVariables.value.contributionCost,
      });
    });

    once('pope_heal_dragon', () => {
      dispatch({
        type: GUIActions.HEAL_DRAGON,
        payload: CONFIG.game.initialDragonHealth.smallDragon * 0.25,
      });
    });

    once('pay_for_catapult', () => {
      dispatch({
        type: GUIActions.SET_GOLD,
        payload: -variablesState.globalVariables.value.catapultCost,
      });

      dispatch({
        type: GUIActions.ADD_GENERATORS,
        payload: {
          catapult: 1,
        },
      });
    });

    const resumeUnsub = on('resume_dialogs_timer', () => {
      unpauseGroup('dialog');
    });

    const enterDungeonUnsub = on(DungeonEvents.ENTER_DUNGEON, () => {
      pauseGroup('dialog');
    });

    const exitDungeonUnsub = on(DungeonEvents.EXIT_DUNGEON, () => {
      unpauseGroup('dialog');
    });

    const purchasedUpgradeUnsub = on(GameplayEvents.PURCHASED_UPGRADE, ({ id, multiplier }) => {
      if (id === UpgradeType.PiercedEardrums) {
        setEventRate('roar', multiplier / 100);
      }
    });

    return () => {
      resumeUnsub();
      roarUnsub();
      enterDungeonUnsub();
      exitDungeonUnsub();
      purchasedUpgradeUnsub();
    };
  }, []);

  useEffect(() => {
    const shootCatapultUnsub = on(GameplayEvents.SHOOT_CATAPULT, () => {
      const playCatSounds = variablesState.globalVariables.value.catSounds;
      if (catapultCatsUpgrade.purchased > 0 && Math.random() > 0.5 && playCatSounds) {
        playSFX(getRandomThing<string>(catsSFXList), 1);
      }
    });

    return () => {
      shootCatapultUnsub();
    };
  }, [catapultCatsUpgrade]);
};
